import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons/faQuestionCircle';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faPlayCircle } from '@fortawesome/pro-solid-svg-icons/faPlayCircle';
import { faStarCircle } from '@fortawesome/pro-solid-svg-icons/faStarCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, type TableColumnsType, theme, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { StatusTag } from 'pos-client/components/generic/status-tag/StatusTag.tsx';
import { useScheduleBoardContext } from 'pos-client/components/scheduleBoard/context/ScheduleBoardContext.tsx';
import { PosTable } from 'pos-client/components/shared/Display/Table/PosTable.tsx';
import { PosTableFilterIcon } from 'pos-client/components/shared/Display/Table/PosTableFilterIcon.tsx';
import { PosTableSortIcon } from 'pos-client/components/shared/Display/Table/PosTableSortIcon.tsx';
import { getUniqueValues } from 'pos-client/components/shared/Display/Table/utils.ts';
import { usePermissions } from 'pos-client/hooks/usePermissions.tsx';
import { useTableData } from 'pos-client/hooks/useTableData.ts';
import { avatarColors } from 'pos-client/utils/colours.ts';
import { timeFormatter } from 'pos-client/utils/dateHelpers.ts';
import { humaniseDuration } from 'pos-client/utils/dayjs.ts';
import type { ScheduleBoardJobModel } from 'pos-shared/models/schedulingSchema.ts';
import { StatusTypeEnum } from 'pos-shared/models/statusSchema.ts';
import {
  getInitials,
  literalToCamelCase,
  normaliseString,
} from 'pos-shared/utils/textHelpers.ts';
import { isEnumValue } from 'pos-shared/utils/zodHelpers.ts';

import { WorkshopBlockedSlotActions } from './WorkshopBlockedSlotActions.tsx';
import { WorkshopJobActions } from './WorkshopJobActions.tsx';

import './WorkshopJobsTable.css';

interface WorkshopJobsTableProps {
  jobs: ScheduleBoardJobModel[];
  isLoading: boolean;
  locationCode?: string | null;
}

export const WorkshopJobsTable = ({
  jobs,
  isLoading,
}: WorkshopJobsTableProps) => {
  const { hasPermissions } = usePermissions();
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { selectedDate } = useScheduleBoardContext();
  const canEditBlockedSlots =
    hasPermissions('edit.authorized.blocked.slot') ||
    hasPermissions('edit.unauthorized.blocked.slot');
  const renderJobStateIcon = useCallback(
    (state: string) => {
      switch (state) {
        case 'in progress':
          return { icon: faPlayCircle, color: token.orange5 };
        case 'complete':
          return { icon: faCheckCircle, color: token.colorSuccessText };
        case 'awaiting caf':
        case 'to do':
          return { icon: faStarCircle, color: token.colorPrimary };
        default:
          return { icon: faQuestionCircle };
      }
    },
    [token.colorPrimary, token.colorSuccessText, token.orange5]
  );

  const renderSopRow = (state: string) => {
    switch (state) {
      case 'in progress':
        return token.orange1;
      case 'to do':
      case 'awaiting caf':
        return token.blue1;
      default:
        return 'transparent';
    }
  };

  const vehicleFilters = useMemo(
    () =>
      getUniqueValues(jobs, 'fulfilmentTo.reference').map((v) => ({
        text: v ?? '',
        value: v ?? '',
      })),
    [jobs]
  );

  const statusOptions = useMemo(
    () =>
      getUniqueValues(jobs, 'state').map((value) => {
        const statusValue = isEnumValue(value, StatusTypeEnum);
        const mappedStatus = statusValue
          ? literalToCamelCase(statusValue)
          : 'unknown';
        return {
          text: statusValue ? t(`statuses.${mappedStatus}`) : value,
          value,
        };
      }),
    [jobs, t]
  );

  const jobFilters = useMemo(
    () =>
      getUniqueValues(jobs, 'jobDescription').map((value) => ({
        text: value ?? '',
        value: value ?? '',
      })),
    [jobs]
  );

  const orderFilters = useMemo(
    () =>
      getUniqueValues(jobs, 'orderReference').map((value) => ({
        text: value ?? '-',
        value: value ?? '-',
      })),
    [jobs]
  );

  const laneFilters = useMemo(
    () =>
      getUniqueValues(jobs, 'laneName').map((value) => ({
        text: value ?? '-',
        value: value ?? '-',
      })),
    [jobs]
  );

  const technicianFilters = useMemo(
    () =>
      [
        ...new Map(
          jobs
            .flatMap((job) => job.technicians ?? [])
            .map((tech) => [tech.userId, tech.name])
        ).values(),
      ].map((name) => ({
        text: name,
        value: name,
      })),
    [jobs]
  );

  const columns: TableColumnsType<ScheduleBoardJobModel> = useMemo(
    () => [
      {
        key: 'jobState',
        title: t('jobs.jobState'),
        align: 'center',
        render: (_, { state }) => (
          <FontAwesomeIcon size="xl" {...renderJobStateIcon(state)} />
        ),
      },
      {
        key: 'orderReference',
        title: t('orders.orderRef'),
        filters: orderFilters,
        onFilter: (value, record) =>
          normaliseString(record?.orderReference ?? '').includes(
            normaliseString(String(value))
          ),
        filterIcon: PosTableFilterIcon,
        render: (_, { orderReference, orderId }) => (
          <Link to={`/orders/manage-orders/${orderId}/summary`} target="_blank">
            {orderReference ?? '-'}
          </Link>
        ),
      },
      {
        key: 'jobReference',
        title: t('jobs.jobRef'),
        dataIndex: 'jobReference',
        render: (_, { jobReference }) => jobReference ?? '-',
      },
      {
        key: 'jobItem',
        title: t('jobs.jobItem'),
        dataIndex: 'jobDescription',
        filters: jobFilters,
        onFilter: (value, record) =>
          normaliseString(record?.jobDescription ?? '').includes(
            normaliseString(String(value))
          ),
        filterIcon: PosTableFilterIcon,
      },
      {
        key: 'vehicle',
        title: t('common.vehicle.vehicle'),
        filters: vehicleFilters,
        onFilter: (value, record) =>
          normaliseString(record.fulfilmentTo?.reference ?? '').includes(
            normaliseString(String(value))
          ),
        render: (_, { fulfilmentTo }) => fulfilmentTo?.reference,
        filterIcon: PosTableFilterIcon,
      },
      {
        key: 'lane',
        title: t('scheduling.lanes.lane'),
        dataIndex: 'laneName',
        filters: laneFilters,
        onFilter: (value, record) =>
          normaliseString(record?.laneName ?? '').includes(
            normaliseString(String(value))
          ),
        filterIcon: PosTableFilterIcon,
      },
      {
        key: 'expStart',
        title: t('common.date.expStartTime'),
        render: (_, { startDateDisplay }) =>
          timeFormatter.format(new Date(startDateDisplay)),
        sorter: (a, b) => {
          const dateA = dayjs(a.startDate);
          const dateB = dayjs(b.startDate);
          return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
        },
        defaultSortOrder: 'ascend',
        sortIcon: (sorted) => (
          <PosTableSortIcon sorted={sorted.sortOrder} type="number" />
        ),
      },
      {
        key: 'duration',
        title: t('common.time.duration'),
        render: (_, d) =>
          `${humaniseDuration(dayjs(d.startDateDisplay), dayjs(d.endDateDisplay), false, ['millisecond', 'second'])}`,
      },
      {
        key: 'technician',
        title: t('technicians.technician'),
        filters: technicianFilters,
        onFilter: (value, record) =>
          record.technicians?.some((tech) =>
            normaliseString(tech.name).includes(normaliseString(String(value)))
          ) ?? false,
        filterIcon: PosTableFilterIcon,
        render: (_, { technicians }) => {
          if (!technicians?.length) return '-';
          return (
            <Avatar.Group size="small">
              {technicians.map(({ userId, name }, index) => (
                <Tooltip title={name} placement="top" key={userId}>
                  <Avatar
                    style={{
                      backgroundColor:
                        avatarColors[index % avatarColors.length],
                    }}
                    gap={4}
                    size="small"
                  >
                    {getInitials(name)}
                  </Avatar>
                </Tooltip>
              ))}
            </Avatar.Group>
          );
        },
      },
      {
        key: 'status',
        title: t('common.status.status'),
        render: (_, { state }) => <StatusTag status={state} />,
        filters: statusOptions,
        onFilter: (value, record) =>
          normaliseString(record.state).includes(
            normaliseString(String(value))
          ),
        filterIcon: PosTableFilterIcon,
      },
      {
        key: 'actions',
        render: (_, record) => {
          if ('jobId' in record) {
            return <WorkshopJobActions job={record} />;
          }
          return canEditBlockedSlots ? (
            <WorkshopBlockedSlotActions blockedSlot={record} />
          ) : null;
        },
      },
    ],
    [
      jobFilters,
      laneFilters,
      orderFilters,
      renderJobStateIcon,
      statusOptions,
      t,
      technicianFilters,
      vehicleFilters,
      canEditBlockedSlots,
    ]
  );

  const tableData = useTableData(
    jobs
      .filter((job) => {
        if (!canEditBlockedSlots) {
          return job.state.toLowerCase() !== 'unauthorized';
        }
        return true;
      })
      .map((job, index) => {
        job.key = `${job.id}-${index}`; //adding key for table rendering
        return job;
      })
      .sort((a, b) => (a.startDateDisplay > b.startDateDisplay ? 1 : -1)),
    (x) => x.jobId
  );
  return (
    <PosTable
      // Key forces remount on date change to clear filter/sort state
      key={selectedDate.toISOString()}
      columns={columns}
      dataSource={tableData}
      isLoading={isLoading}
      onRow={(d) => ({ style: { background: renderSopRow(d.state) } })}
    />
  );
};
